import { useMemo, useState } from 'react';
import * as R from 'remeda';
import { SchemaSection } from '@rossum/api-client/schemas';
import { Collapse, Stack } from '@rossum/ui/material';
import { getUnsafe } from '../../../../../../lib/helpers';
import { isNotNullOrUndefined } from '../../../../../../lib/typeGuards';
import useDebounce from '../../../../../../utils/hooks/useDebounce';
import { useGetDatasetDetails } from '../../../../../datasets/hooks/useGetDatasetDetails';
import { useTables } from '../../../../../datasets/hooks/useTables';
import { getColumnsFromMetadata } from '../../../../../datasets/types';
import { useIsLookupAgentEnabled } from '../useIsLookupAgentEnabled';
import { AgentLookupEditor } from './AgentLookupEditor';
import { AggregateEditor } from './AggregateEditor';
import { LegacyLookupEditor } from './LegacyLookupEditor';
import { ComplexDataLookupPayload, QueryObject, VariablesMap } from './types';

type ComplexDataLookupProps = {
  value: ComplexDataLookupPayload;
  onChange: (value: ComplexDataLookupPayload) => void;
  schemaContent: SchemaSection[];
  lookupSchemaId: string;
  lookupLabel: string;
};

/**
 * Derives what both editor variants need, then hands over to whichever one the feature gate picks.
 * The JSON editor below them is shared, so its state stays here.
 */
export const ComplexDataLookup = ({
  value,
  onChange,
  schemaContent,
  lookupSchemaId,
  lookupLabel,
}: ComplexDataLookupProps) => {
  const isLookupAgentEnabled = useIsLookupAgentEnabled();
  const [isSourceEditorOpen, setIsSourceEditorOpen] = useState(false);

  // Prevents recomputing summaries when editing via JSON editor
  const [frozenSummaryValue, setFrozenSummaryValue] = useState<{
    queries: QueryObject[];
    variables: VariablesMap;
  } | null>(null);

  // `value` reaches us through a cast (`value as ComplexDataLookupPayload` in
  // CustomLookup.tsx) that promises queries and variables exist where the schema
  // — z.record(z.unknown()) — guarantees nothing.
  const currentSummaryValue = useMemo(
    () => ({
      queries: value.queries ?? [],
      variables: value.variables ?? {},
    }),
    [value.queries, value.variables]
  );

  const summaryValue = frozenSummaryValue ?? currentSummaryValue;

  const { data: tables } = useTables();

  const table = useMemo(
    () => tables?.find(t => t.name === value.dataset) ?? null,
    [tables, value.dataset]
  );

  const stableTables = useMemo(() => tables ?? [], [tables]);

  const { data } = useGetDatasetDetails({
    name: table?.name,
    limit: 5,
  });

  const columnOptions = useMemo(
    () =>
      table
        ? getColumnsFromMetadata(table.metadata, table.schema).map(c => ({
            ...c,
            samples:
              data?.results
                .map(r => getUnsafe(r, c.datasetAttributeName))
                .filter(isNotNullOrUndefined)
                .map(sample =>
                  R.truncate(
                    typeof sample === 'string'
                      ? sample
                      : JSON.stringify(sample),
                    50
                  )
                ) ?? [],
          }))
        : [],
    [data?.results, table]
  );

  // Debounce the field ID together with the schema so the summary payload is
  // always internally consistent — a fresh ID paired with a not-yet-debounced
  // schema is rejected by the summary endpoint (field not found in schema)
  // and the summary tiles then stay blank until remount.
  const summaryPayload = useMemo(
    () => ({ schemaContent, fieldSchemaId: lookupSchemaId }),
    [schemaContent, lookupSchemaId]
  );
  const debouncedSummaryPayload = useDebounce(summaryPayload, 500);

  const editorProps = {
    value,
    onChange,
    schemaContent,
    lookupSchemaId,
    lookupLabel,
    isRequiredInfoMissing: lookupLabel === '' || lookupSchemaId === '',
    table,
    tables: stableTables,
    columnOptions,
    summaryPayload: debouncedSummaryPayload,
    summaryValue,
    isSourceEditorOpen,
    onToggleSourceEditor: () => setIsSourceEditorOpen(open => !open),
  };

  return (
    <Stack spacing={3}>
      {isLookupAgentEnabled ? (
        <AgentLookupEditor {...editorProps} />
      ) : (
        <LegacyLookupEditor {...editorProps} />
      )}
      <Collapse in={isSourceEditorOpen} mountOnEnter>
        <AggregateEditor
          value={value}
          onChange={({ queries, variables }) => {
            if (
              !R.isDeepEqual(queries, value.queries) ||
              !R.isDeepEqual(variables, value.variables)
            ) {
              onChange({
                ...value,
                queries,
                variables,
              });
            }
          }}
          onFocus={() => {
            if (currentSummaryValue.queries.length > 0) {
              setFrozenSummaryValue(currentSummaryValue);
            }
          }}
          onBlur={() => {
            setFrozenSummaryValue(null);
          }}
          schemaContent={schemaContent}
          columns={columnOptions}
        />
      </Collapse>
    </Stack>
  );
};
